import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPlanLlmPrompt, DialogPlanLlmPromptApi, DialogPlanLlmPromptSpeakWithGrade } from "../../api/DialogPlanLlmPromptApi";
import AppBarContext from '../../context/AppBarContext';
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPlanLlmPromptSpeakWithGradeFormProps {
    dialogPlanLlmPromptId?: number;
    dialogPlanLlmPrompt?: DialogPlanLlmPromptSpeakWithGrade;
    setDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleLlmPromptChange?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleRegenerateDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => void;
    isRegenerating?: boolean;

    id: number;
    dialogPlanId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    yesResponse: string | null;
    noResponse: string | null;
    imageSource: string | null;
}

export const DialogPlanLlmPromptSpeakWithGradeForm: React.FC<DialogPlanLlmPromptSpeakWithGradeFormProps> = ({
    dialogPlanLlmPromptId, dialogPlanLlmPrompt, setDialogPlanLlmPrompt, handleLlmPromptChange, handleRegenerateDialogPlanLlmPrompt, isRegenerating,
    id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, noResponse, imageSource }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [teacherChinesePrompt, setTeacherChinesePrompt] = React.useState("");
    const [studentResponsePrompt, setStudentResponsePrompt] = React.useState("<Answer/>");
    const [teacherHintPrompt, setTeacherHintPrompt] = React.useState("");
    const [dialogPromptSentenceList, setDialogPromptSentenceList] = React.useState("");
    const handleTeacherChinesePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTeacherChinesePrompt(e.target.value); };
    const handleStudentResponsePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setStudentResponsePrompt(e.target.value); };
    const handleTeacherHintPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTeacherHintPrompt(e.target.value); };
    const handleDialogPromptSentenceListChange = (e: React.ChangeEvent<HTMLInputElement>) => { setDialogPromptSentenceList(e.target.value); };

    const hasLoadedDialogPlanLlmPromptFields = React.useRef(false);

    const setDialogPlanLlmPromptFields = React.useCallback((dialogPlanLlmPrompt: DialogPlanLlmPromptSpeakWithGrade) => {
        if (dialogPlanLlmPrompt.class_name == "DialogPlanLlmPromptSpeakWithGradeModel") {
            setTeacherChinesePrompt(dialogPlanLlmPrompt.teacher_chinese_prompt ? dialogPlanLlmPrompt.teacher_chinese_prompt : "")
            setStudentResponsePrompt(dialogPlanLlmPrompt.student_response_prompt ? dialogPlanLlmPrompt.student_response_prompt : "")
            setTeacherHintPrompt(dialogPlanLlmPrompt.teacher_hint_prompt ? dialogPlanLlmPrompt.teacher_hint_prompt : "")
            setDialogPromptSentenceList(dialogPlanLlmPrompt.dialog_prompt_sentence_list ? dialogPlanLlmPrompt.dialog_prompt_sentence_list : "")
            hasLoadedDialogPlanLlmPromptFields.current = true;
        }
    }, []);

    React.useEffect(() => {
        if (!dialogPlanLlmPromptId && dialogPlanLlmPrompt) {
            setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
        }
    }, [dialogPlanLlmPrompt, dialogPlanLlmPromptId, setDialogPlanLlmPromptFields])
    React.useEffect(() => {
        if (dialogPlanLlmPromptId && !dialogPlanLlmPrompt) {
            DialogPlanLlmPromptApi.getById(dialogPlanLlmPromptId)
                .then((response) => {
                    const dialogPlanLlmPrompt = response.data as DialogPlanLlmPromptSpeakWithGrade;
                    setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [dialogPlanLlmPromptId, dialogPlanLlmPrompt, history, location, setErrorMessage, setDialogPlanLlmPromptFields])

    const effectiveTeacherChinesePrompt = hasLoadedDialogPlanLlmPromptFields.current
        ? teacherChinesePrompt
        : dialogPlanLlmPrompt?.teacher_chinese_prompt ?? teacherChinesePrompt;
    const effectiveStudentResponsePrompt = hasLoadedDialogPlanLlmPromptFields.current
        ? studentResponsePrompt
        : dialogPlanLlmPrompt?.student_response_prompt ?? studentResponsePrompt;
    const effectiveTeacherHintPrompt = hasLoadedDialogPlanLlmPromptFields.current
        ? teacherHintPrompt
        : dialogPlanLlmPrompt?.teacher_hint_prompt ?? teacherHintPrompt;
    const effectiveDialogPromptSentenceList = hasLoadedDialogPlanLlmPromptFields.current
        ? dialogPromptSentenceList
        : dialogPlanLlmPrompt?.dialog_prompt_sentence_list ?? dialogPromptSentenceList;

    const currentDialogPlanLlmPrompt = React.useMemo(() => (
        new DialogPlanLlmPromptSpeakWithGrade(
            id,
            dialogPlanId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            yesResponse as string | "ERROR",
            noResponse as string | "ERROR",
            imageSource,
            effectiveTeacherChinesePrompt,
            [],  // TODO pinyin
            effectiveStudentResponsePrompt,
            effectiveTeacherHintPrompt,
            effectiveDialogPromptSentenceList,
        )
    ), [
        id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, noResponse, imageSource,
        effectiveTeacherChinesePrompt, effectiveStudentResponsePrompt, effectiveTeacherHintPrompt, effectiveDialogPromptSentenceList,
    ]);

    React.useEffect(() => {
        if (handleLlmPromptChange) {
            handleLlmPromptChange(currentDialogPlanLlmPrompt);
        }
        if (setDialogPlanLlmPrompt) {
            setDialogPlanLlmPrompt(currentDialogPlanLlmPrompt);
        }
    }, [currentDialogPlanLlmPrompt, handleLlmPromptChange, setDialogPlanLlmPrompt])

    return (
        <Stack spacing={4} sx={{
            width: dialogPlanLlmPrompt ? '100%' : '90%', // Conditionally set width
            margin: '0 auto', pt: 2, pb: 4
        }}>

            <TextField
                label="Llm instructions for Teacher Chinese Prompt"
                value={teacherChinesePrompt}
                onChange={handleTeacherChinesePromptChange}
                multiline
                rows={5}
            />
            {/* hidden spacer field */}
            <TextField
                label="Teacher Chinese Prompt Pinyin"
                sx={{ opacity: 0 }}
            // value={teacherChinesePromptPinyin}
            // onChange={handleTeacherChinesePromptPinyinChange}
            // required
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "teacher_chinese_prompt")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}
            <div>
                <TextField
                    label="Llm instructions for Student Chinese Prompt"
                    value={studentResponsePrompt ? studentResponsePrompt : "<Answer/>"}
                    onChange={handleStudentResponsePromptChange}
                    required
                    sx={{ width: '100%' }}
                    multiline
                    rows={5}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Use &lt;Answer/&gt; to mark where the response is inserted
                </Typography>
                {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button variant="contained"
                            onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "student_response_prompt")}
                            disabled={isRegenerating}>
                            Regenerate
                            {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                        </Button>
                    </Box>
                </>}
            </div>
            <TextField
                label="Llm instructions for Teacher Hint Prompt"
                value={teacherHintPrompt}
                onChange={handleTeacherHintPromptChange}
                multiline
                rows={5}
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "teacher_hint_prompt")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}
            <TextField
                label="LLM Prompt for Allowed Student Prompt Responses"
                value={dialogPromptSentenceList}
                onChange={handleDialogPromptSentenceListChange}
                multiline
                rows={5}
                sx={{ width: '100%' }}
                required
            />
            {/* hidden spacer field */}
            <TextField
                label="Allowed Student Prompt Response Pinyin"
                // value={studentResponsePinyinList}
                // onChange={handleStudentResponsePinyinListChange}
                multiline
                rows={6}
                sx={{ width: '100%', opacity: 0 }}
            // required
            // error={studentResponsePinyinErrorText != null}
            // helperText={studentResponsePinyinErrorText}
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "dialog_prompt_sentence_list")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}
        </Stack>
    )
}
